import { DateOnly } from '@if/core/date'
import {
  ControlledCheckbox,
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledSelect,
  ControlledTextField,
  ControlledToggleButtonGroup,
  useDollarAdornment,
  useTranslatedOptionEnum,
} from '@if/mui'
import { Stack, ToggleButton, Typography } from '@mui/material'
import FormDialog from '@src/components/Common/Dialog/FormDialog'
import { useUser } from '@src/providers/UserProvider'
import { useUnmountableDialog } from '@src/services/dialog'
import { SafePaymentActionDateService } from '@src/services/SafePaymentActionDateService'
import { JSX, useCallback, useMemo, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import EditPaymentCloseAllBtn from '../EditPaymentCloseAllBtn'
import { EditPaymentSchemaType } from '../EditPayments-schema'
import {
  EAddRecurringPaymentOption,
  getAddRecurringPaymentSupportedFrequencies,
  useAddRecurringPaymentsResolver,
} from '../LoanAddRecurringPayment/LoanAddRecurringPayment-schema'
import {
  createFirstPaymentForRecurring,
  generateRecurringPaymentByEndDate,
  generateRecurringPaymentByNumberOfPayments,
} from './LoanAddRecurringPayment-helpers'

interface LoanAddRecurringPaymentDialogProps {
  onConfirm: (data: EditPaymentSchemaType[]) => void
  onCloseAll: () => void
  onClose: () => void
  paymentMethodOptions: JSX.Element[]
  loanActivationDate: DateOnly
  loanEndDate: DateOnly
}

const LoanAddRecurringPaymentDialog = ({
  onConfirm,
  onClose,
  onCloseAll,
  paymentMethodOptions,
  loanActivationDate,
  loanEndDate,
}: LoanAddRecurringPaymentDialogProps) => {
  const { t } = useTranslation()
  const user = useUser()
  const canBypassAcpLock = user?.rights.canEditLockedPpaPayments ?? false
  const [isPending, startTransition] = useTransition()
  const { resolver, defaultValues } = useAddRecurringPaymentsResolver(loanActivationDate, loanEndDate)
  const { control, setValue, watch, handleSubmit } = useForm({
    resolver,
    defaultValues,
  })

  const frequencyOptions = useTranslatedOptionEnum(
    'EPaymentFrequency',
    getAddRecurringPaymentSupportedFrequencies(),
    'enum',
  )
  const dollarAdornment = useDollarAdornment()
  const { dialogProps, closeDialog } = useUnmountableDialog(onClose, isPending)

  const handleConfirm = handleSubmit((data) => {
    startTransition(() => {
      const firstRecurringPayment = createFirstPaymentForRecurring(data)
      switch (data.option) {
        case EAddRecurringPaymentOption.NumberOfPayments:
          onConfirm(
            generateRecurringPaymentByNumberOfPayments(firstRecurringPayment, data.frequency, data.numberOfPayments!),
          )
          break
        case EAddRecurringPaymentOption.EndDate:
          onConfirm(generateRecurringPaymentByEndDate(firstRecurringPayment, data.frequency, data.endDate!))
          break
        default:
          throw new Error('Unknown option')
      }
      closeDialog()
    })
  })

  const [option, paymentMethod] = watch(['option', 'paymentMethod'])

  const handleOnOptionChange = useCallback(
    (value: string | null) => {
      if (value) {
        if ((value as EAddRecurringPaymentOption) === EAddRecurringPaymentOption.EndDate) {
          setValue('numberOfPayments', null)
        } else if ((value as EAddRecurringPaymentOption) === EAddRecurringPaymentOption.NumberOfPayments) {
          setValue('endDate', null)
        }
      }
    },
    [setValue],
  )

  const minStartDate = useMemo(
    () =>
      SafePaymentActionDateService.getInstance().getMinPaymentDate(
        paymentMethod,
        loanActivationDate,
        canBypassAcpLock,
      ),
    [paymentMethod, canBypassAcpLock, loanActivationDate],
  )

  return (
    <FormDialog
      {...dialogProps}
      onConfirm={handleConfirm}
      title={t('loan.payment.addRecurringPayments.title')}
      additionalActions={<EditPaymentCloseAllBtn onCloseAll={onCloseAll} />}
      loading={isPending}
    >
      <Stack spacing={1}>
        <ControlledNumberField
          control={control}
          name="amount"
          slotProps={{ input: dollarAdornment }}
          label={t('loan.payment.addRecurringPayments.amount')}
          autoFocus
        />
        <Stack direction="row" spacing={1}>
          <ControlledSelect
            control={control}
            name="frequency"
            options={frequencyOptions}
            label={t('loan.payment.addRecurringPayments.frequency')}
            disableEmptyOption
          />
          <ControlledDateOnlyPicker
            control={control}
            name="startDate"
            label={t('loan.payment.addRecurringPayments.startDate')}
            minDate={minStartDate}
          />
        </Stack>

        <div>
          <Typography variant="subtitle1">{t('loan.payment.addRecurringPayments.options.label')}</Typography>
          <ControlledToggleButtonGroup
            control={control}
            name="option"
            exclusive
            required
            fullWidth
            color="primary"
            onChange={handleOnOptionChange}
          >
            <ToggleButton value={EAddRecurringPaymentOption.NumberOfPayments}>
              {t('loan.payment.addRecurringPayments.options.numberOfPayments')}
            </ToggleButton>
            <ToggleButton value={EAddRecurringPaymentOption.EndDate}>
              {t('loan.payment.addRecurringPayments.options.endDate')}
            </ToggleButton>
          </ControlledToggleButtonGroup>
        </div>
        {option === EAddRecurringPaymentOption.NumberOfPayments && (
          <ControlledNumberField
            control={control}
            name="numberOfPayments"
            label={t('loan.payment.addRecurringPayments.numberOfPayments')}
            disableDecimals
          />
        )}
        {option === EAddRecurringPaymentOption.EndDate && (
          <ControlledDateOnlyPicker
            control={control}
            name="endDate"
            label={t('loan.payment.addRecurringPayments.endDate')}
          />
        )}
        <ControlledSelect
          control={control}
          name="paymentMethod"
          options={paymentMethodOptions}
          label={t('loan.paymentMethod')}
          disableEmptyOption
        />
        <ControlledTextField control={control} name="note" label="Note" />
        <ControlledCheckbox control={control} name="sendSmsReminder" label={t('loan.payment.sendSMSReminder')} />
      </Stack>
    </FormDialog>
  )
}

export default LoanAddRecurringPaymentDialog
